import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BASE = [
  { id: "today", label: "Hari" },
  { id: "week", label: "Minggu" },
  { id: "month", label: "Bulan" },
  { id: "year", label: "Tahun" },
  { id: "all", label: "Semua" },
];

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const SELECT_CLS = "h-10 sm:h-8 flex-1 sm:flex-none rounded-md border border-input bg-background px-2 text-xs";

// Deteksi range custom yang mewakili satu bulan penuh / satu tahun penuh,
// supaya setelah refresh pilihan Bulan/Tahun tetap tampil menyala.
const detectSubSel = (value, range) => {
  if (value !== "custom" || !range?.start || !range?.end) return null;
  const [sy, sm] = range.start.split("-").map(Number);
  const [ey, em, ed] = range.end.split("-").map(Number);
  if (!sy || !sm || !ey || !em || !ed) return null;
  const lastDay = new Date(Date.UTC(sy, sm, 0)).getUTCDate();
  if (sy === ey && sm === em && range.start.endsWith("-01") && ed === lastDay) return "month";
  if (range.start === `${sy}-01-01` && range.end === `${sy}-12-31`) return "year";
  return null;
};

export const PeriodFilter = ({ value, onChange, range, onRangeChange }) => {
  const now = new Date();
  const thisYear = now.getFullYear();
  const YEARS = [];
  for (let y = 2019; y <= thisYear + 1; y++) YEARS.push(y);

  const periods = onRangeChange ? [...BASE, { id: "custom", label: "Custom" }] : BASE;
  const [manualSubSel, setManualSubSel] = useState(null);
  const [monthPick, setMonthPick] = useState({ m: now.getMonth(), y: thisYear });
  const [yearPick, setYearPick] = useState(thisYear);

  const canPick = !!(range && onRangeChange);
  // "custom" di manualSubSel = user eksplisit klik Custom, tampilkan input tanggal mentah
  const subSel = manualSubSel !== null ? manualSubSel : (canPick ? detectSubSel(value, range) : null);

  // Nilai dropdown mengikuti range agar tetap benar setelah refresh.
  const dispMonth = subSel === "month" && range?.start
    ? { m: Number(range.start.split("-")[1]) - 1, y: Number(range.start.split("-")[0]) }
    : (value === "month" ? { m: now.getMonth(), y: thisYear } : monthPick);
  const dispYear = subSel === "year" && range?.start
    ? Number(range.start.split("-")[0])
    : (value === "year" ? thisYear : yearPick);

  const applyMonth = (m, y) => {
    setMonthPick({ m, y });
    if (!canPick) return;
    const first = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const last = new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);
    setManualSubSel("month");
    onRangeChange({ start: first, end: last });
    onChange("custom");
  };

  const applyYear = (y) => {
    setYearPick(y);
    if (!canPick) return;
    setManualSubSel("year");
    onRangeChange({ start: `${y}-01-01`, end: `${y}-12-31` });
    onChange("custom");
  };

  const isActive = (id) => (subSel ? subSel === id : value === id);
  const showMonthPick = canPick && ((value === "month" && !subSel) || subSel === "month");
  const showYearPick = canPick && ((value === "year" && !subSel) || subSel === "year");
  const showCustomRange = canPick && value === "custom" && (!subSel || subSel === "custom");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex flex-wrap gap-1 rounded-xl bg-secondary p-1">
        {periods.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant={isActive(p.id) ? "default" : "ghost"}
            onClick={() => { setManualSubSel(p.id === "custom" && canPick ? "custom" : null); onChange(p.id); }}
            data-testid={`filter-period-${p.id}`}
            className={`h-8 px-3 text-xs font-medium ${isActive(p.id) ? "shadow-sm" : "text-muted-foreground"}`}
          >
            {p.label}
          </Button>
        ))}
      </div>
      {showMonthPick && (
        <div className="flex w-full sm:w-auto items-center gap-1.5">
          <select
            data-testid="filter-month-select"
            className={SELECT_CLS}
            value={dispMonth.m}
            onChange={(e) => applyMonth(Number(e.target.value), dispMonth.y)}
          >
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select
            data-testid="filter-month-year-select"
            className={SELECT_CLS}
            value={dispMonth.y}
            onChange={(e) => applyMonth(dispMonth.m, Number(e.target.value))}
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      {showYearPick && (
        <select
          data-testid="filter-year-select"
          className={`${SELECT_CLS} w-full sm:w-auto`}
          value={dispYear}
          onChange={(e) => applyYear(Number(e.target.value))}
        >
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      )}
      {showCustomRange && (
        <div className="flex w-full sm:w-auto items-center gap-1.5">
          <Input type="date" data-testid="filter-range-start" value={range.start}
            onChange={(e) => onRangeChange({ ...range, start: e.target.value })} className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-[150px] text-xs" />
          <span className="text-muted-foreground text-xs">s/d</span>
          <Input type="date" data-testid="filter-range-end" value={range.end}
            onChange={(e) => onRangeChange({ ...range, end: e.target.value })} className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-[150px] text-xs" />
        </div>
      )}
    </div>
  );
};
